import { useCallback, useEffect, useRef, useState } from 'react';

const useSendTabNotifications = ({ onNotify }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', key: 0 });
  const isMountedRef = useRef(true);

  const showSnackbar = useCallback((message, severity = 'success') => {
    const nextKey = Date.now();
    console.debug('[SendSelection] snackbar:open', { message, severity, key: nextKey });
    setSnackbar({ open: true, message, severity, key: nextKey });
  }, []);

  const emitToast = useCallback(
    (message, severity = 'success', options = {}) => {
      if (!isMountedRef.current) {
        console.debug('[SendSelection] emitToast skipped (unmounted)', { message, severity, options });
        return;
      }

      console.debug('[SendSelection] emitToast', {
        message,
        severity,
        hasParentNotify: Boolean(onNotify),
        parentNotifyType: typeof onNotify,
        options,
      });

      // Always show local snackbar first so failures in the parent callback do not suppress feedback.
      showSnackbar(message, severity);

      if (onNotify) {
        try {
          // Always notify the parent so it can trigger post-send refreshes,
          // but keep snackbars local to the Send tab (avoids duplicate toasts).
          onNotify(message, severity, { ...options, suppressSnackbar: true });
          console.debug('[SendSelection] parent notify ok', { message, severity });
        } catch (err) {
          console.error('[SendSelection] parent notify failed', err);
        }
      }
    },
    [onNotify, showSnackbar]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    // React 18 StrictMode intentionally mounts + unmounts effects in dev to surface unsafe side effects.
    // Ensure we reset the mount flag on each effect run, otherwise cleanup can permanently flip it false.
    isMountedRef.current = true;
    console.debug('[SendSelection] mount flag set', { isMounted: isMountedRef.current });
    return () => {
      isMountedRef.current = false;
      console.debug('[SendSelection] mount flag cleared', { isMounted: isMountedRef.current });
    };
  }, []);

  return {
    snackbar,
    showSnackbar,
    emitToast,
    handleCloseSnackbar,
    isMountedRef,
  };
};

export default useSendTabNotifications;
